import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { ArrowLeft, Crown, Search, Users, ShieldOff, BarChart3, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { toast, Toaster } from 'sonner';
import AdminStatusStrip from '../components/AdminStatusStrip';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PAGE_SIZE = 50;

const AdminUsers = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [actingOn, setActingOn] = useState(null);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/users`, {
        params: { skip, limit: PAGE_SIZE, search: search || undefined },
        withCredentials: true,
      });
      setUsers(res.data.users);
      setTotal(res.data.total);
    } catch (err) {
      toast.error(t('admin.loadError'));
    } finally {
      setLoading(false);
    }
  }, [skip, search, t]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const handlePromote = async (user) => {
    setActingOn(user.id);
    try {
      await axios.post(`${API}/admin/users/${user.id}/promote`, {}, { withCredentials: true });
      toast.success(t('admin.promotedToast', { email: user.email }));
      loadUsers();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('admin.planChangeError'));
    } finally {
      setActingOn(null);
    }
  };

  const handleDemote = async (user) => {
    setActingOn(user.id);
    try {
      await axios.post(`${API}/admin/users/${user.id}/demote`, {}, { withCredentials: true });
      toast.success(t('admin.demotedToast', { email: user.email }));
      loadUsers();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('admin.planChangeError'));
    } finally {
      setActingOn(null);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '—';
    try {
      const locale = i18n.resolvedLanguage?.startsWith('en') ? 'en-GB' : 'pl-PL';
      return new Date(iso).toLocaleDateString(locale, { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch {
      return iso;
    }
  };

  const renderRowAction = (u) => {
    if (u.role === 'admin') {
      return <span className="hussar-admin-locked">—</span>;
    }
    if (u.subscription_status === 'premium') {
      return (
        <Button
          onClick={() => handleDemote(u)}
          disabled={actingOn === u.id}
          className="hussar-btn-secondary hussar-btn-sm"
          data-testid={`admin-demote-${u.id}`}
          title={t('admin.demoteTitle')}
        >
          {actingOn === u.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldOff className="w-4 h-4" />}
          <span>{t('admin.demoteToFree')}</span>
        </Button>
      );
    }
    return (
      <Button
        onClick={() => handlePromote(u)}
        disabled={actingOn === u.id}
        className="hussar-btn-primary hussar-btn-sm"
        data-testid={`admin-promote-${u.id}`}
        title={t('admin.promoteTitle')}
      >
        {actingOn === u.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Crown className="w-4 h-4" />}
        <span>{t('admin.promoteToPremium')}</span>
      </Button>
    );
  };

  const renderBody = () => {
    if (loading) {
      return (
        <div className="hussar-loading">
          <Loader2 className="w-8 h-8 animate-spin hussar-loader" />
          <p>{t('admin.loading')}</p>
        </div>
      );
    }
    if (users.length === 0) {
      return <p className="hussar-mode-empty">{t('admin.emptyResults')}</p>;
    }
    return (
      <div className="hussar-admin-table-wrap">
        <table className="hussar-admin-table" data-testid="admin-users-table">
          <thead>
            <tr>
              <th>{t('admin.thEmail')}</th>
              <th>{t('admin.thName')}</th>
              <th>{t('admin.thPlan')}</th>
              <th>{t('admin.thPromptsToday')}</th>
              <th>{t('admin.thJoined')}</th>
              <th>{t('admin.thRole')}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} data-testid={`admin-user-row-${u.id}`}>
                <td className="hussar-admin-email">{u.email}</td>
                <td>{u.name || '—'}</td>
                <td>
                  <span className={`hussar-admin-badge ${u.subscription_status === 'premium' ? 'hussar-admin-badge-premium' : 'hussar-admin-badge-free'}`}>
                    {u.subscription_status === 'premium' ? t('admin.badgePremium') : t('admin.badgeFree')}
                  </span>
                </td>
                <td className="hussar-admin-num">{u.daily_prompts_used ?? 0}</td>
                <td className="hussar-admin-date">{formatDate(u.created_at)}</td>
                <td>{u.role === 'admin' ? <span className="hussar-admin-role">{t('admin.roleAdmin')}</span> : '—'}</td>
                <td className="hussar-admin-actions">{renderRowAction(u)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="hussar-app-page">
      <Toaster theme="dark" position="top-center" />

      <header className="hussar-app-header">
        <div className="hussar-app-header-content">
          <Button
            onClick={() => navigate('/app')}
            className="hussar-btn-ghost hussar-btn-icon"
            data-testid="admin-back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="hussar-app-header-title">
            <Users className="w-6 h-6" />
            <h1>{t('admin.title')}</h1>
          </div>
          <Button
            onClick={() => navigate('/analytics')}
            className="hussar-btn-ghost hussar-btn-icon"
            data-testid="admin-analytics-btn"
            title={t('admin.analyticsTitle')}
          >
            <BarChart3 className="w-5 h-5" />
          </Button>
        </div>
      </header>

      <div className="hussar-app-main">
        <div className="hussar-analytics-container">
          <AdminStatusStrip />
          <Card className="hussar-analytics-card hussar-analytics-card-wide">
            <div className="hussar-admin-toolbar">
              <div className="hussar-admin-search">
                <Search className="w-4 h-4 hussar-admin-search-icon" />
                <Input
                  type="text"
                  value={search}
                  onChange={(e) => { setSkip(0); setSearch(e.target.value); }}
                  placeholder={t('admin.searchPlaceholder')}
                  className="hussar-form-input"
                  data-testid="admin-search-input"
                />
              </div>
              <div className="hussar-admin-total">
                <span>{t('admin.totalLabel')}</span>
                <strong data-testid="admin-total">{total}</strong>
              </div>
            </div>

            {renderBody()}

            {total > PAGE_SIZE && (
              <div className="hussar-admin-pagination">
                <Button
                  onClick={() => setSkip(Math.max(0, skip - PAGE_SIZE))}
                  disabled={skip === 0 || loading}
                  className="hussar-btn-secondary hussar-btn-sm"
                  data-testid="admin-prev-page"
                >
                  {t('admin.prevPage')}
                </Button>
                <span className="hussar-admin-page-info">
                  {skip + 1}–{Math.min(skip + PAGE_SIZE, total)} / {total}
                </span>
                <Button
                  onClick={() => setSkip(skip + PAGE_SIZE)}
                  disabled={skip + PAGE_SIZE >= total || loading}
                  className="hussar-btn-secondary hussar-btn-sm"
                  data-testid="admin-next-page"
                >
                  {t('admin.nextPage')}
                </Button>
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
};

export default AdminUsers;
